import { Feather } from 'lucide-react';
import '../../styles/components/morphing-status-loader.css';

/**
 * MorphingStatusLoader
 * A premium, high-fidelity animated loader that morphs between a brand label,
 * a laptop icon, and a status checkmark.
 * 
 * @param {string} label - The text displayed in the first phase (default: "PlayPay").
 * @param {string} accentColor - The primary brand color (default: "#16A34A").
 * @param {string} size - The base font size to scale the component (default: "16px").
 * @param {boolean} centered - Whether to wrap the loader in a full-page centering overlay.
 * @param {string} className - Additional CSS classes for the root element.
 * @param {object} style - Inline styles for the root element.
 */
export const MorphingStatusLoader = ({
  label = 'PlayPay',
  accentColor = '#16A34A',
  size = '16px',
  centered = false,
  className = '',
  style = {}
}) => {
  const rootStyles = {
    '--loader-accent': accentColor,
    fontSize: size,
    ...style
  };

  const loaderContent = (
    <div
      className={`loader-root ${className}`}
      style={rootStyles}
      role="status"
      aria-live="polite"
      aria-label={`Loading ${label}...`}
    >
      {/* Phase 1: Brand & Pulse */}
      <div className="loader-ph1">
        <div className="loader-record">
          <Feather className="loader-record-icon" />
        </div>
        <div className="loader-record-text">{label}</div>
      </div>

      {/* Phase 2: Device Morph */}
      <div className="loader-ph2">
        <div className="loader-laptop-b" />
        <svg
          className="loader-laptop-t"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 42 30"
          aria-hidden="true"
        >
          <path
            d="M21 1H5C2.78 1 1 2.78 1 5V25a4 4 90 004 4H37a4 4 90 004-4V5c0-2.22-1.8-4-4-4H21"
            pathLength={100}
            strokeWidth={2}
            stroke="currentColor"
            fill="none"
          />
        </svg>
      </div>

      {/* Phase 3: Status Completion */}
      <div className="loader-icon" />
    </div>
  );

  if (centered) {
    return (
      <div 
        style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(8px)',
          zIndex: 9999
        }}
        className="loader-overlay"
      >
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
};

export default MorphingStatusLoader;
